import React from 'react';
import {
  Image,
  PageContent,
  PageElement,
  Title4,
  TextLarge,
  SecondaryButton,
  ListBasic,
  ListItemBasic,
  Link,
  Icon,
} from '@hz-design-system/web-ui';
import { Trans, useI18nContext } from '@ecg-marktplaats/js-react-i18n';

import { trackGAEvent } from '../../../utils/gaTracking';
import SellBlock from '../../SellBlock/components/SellBlock';
import { EnvironmentProvider } from '../../../contexts/EnvironmentContext';
import SellSteps from './SellSteps';
import SellValueChecker from './SellValueChecker';
import SellReasons from './SellReasons';
import SellTips from './SellTips';
import SellFaqList from '../../SellFaqList/SellFaqList';
import SellNudge from './SellNudge';

import Classes from './SellPageContent.scss';
import { TEnvConfigCars } from 'types/TServerContext';
import carsLocations from '../../../../../../content/carsContent/locations.json';

type TSellPageContentProps = {
  environment: TEnvConfigCars;
  appState: any;
};

const SellPageContent = ({ environment, appState }: TSellPageContentProps) => {
  const { t } = useI18nContext();
  const {
    links: { imagesSources },
    tenantContext: { tenant },
    experiments: { isCarsSellPageEnabled },
  } = environment;
  const intent = environment.intent as string;
  const { location } = appState;
  const isMp = tenant === 'mp';

  // {location ? t('cars.page.sell.location.h1', { location: location.name }) : t('page.title.sell.cars')}

  return (
    <EnvironmentProvider value={environment}>
      <PageElement className={Classes.heroSection}>
        <PageContent className={Classes.hero}>
          <div className={Classes.heroContent}>
            <h1 className={Classes.pageTitle}>
              <span className={Classes.heroHeaderLine}>{t(`page.title.${intent}.line1`)}</span>
              <span className={Classes.heroHeaderLine}>{t(`page.title.${intent}.line2`)}</span>
            </h1>
            <TextLarge as="h2">{t(`page.subtitle.${intent}`)}</TextLarge>
            <ul className={Classes.heroUsps}>
              <li className={Classes.heroUsp}>
                <Icon className={Classes.heroUspIcon} name="profile" color="text-brand-primary" />
                <Trans tagName="span" i18nKey={`page.usp1.${intent}`} />
              </li>
            </ul>
          </div>
          <div className={`${Classes.heroSell} PageContent-card`}>
            <SellBlock withLicensePlateField={appState.withLicensePlateField} />
          </div>
        </PageContent>
      </PageElement>
      <PageElement>
        <PageContent>
          <section className="PageContent-card PageContent-offset">
            <div className={Classes.contentCard}>
              <div className={Classes.contentBody}>
                <Title4 utilities={['marginBottomS']}>{t('sell.content.cars.title')}</Title4>
                <TextLarge utilities={['marginBottomXxs']}>{t('sell.content.cars.body1')}</TextLarge>
                <TextLarge utilities={['marginBottomL']}>{t('sell.content.cars.body2')}</TextLarge>
                <SecondaryButton
                  as="a"
                  href="/plaats/91"
                  onClick={() => {
                    trackGAEvent('placeAdButtonClicked', 'article');
                  }}
                >
                  {t('cars.sell.place_ad_button2')}
                </SecondaryButton>
              </div>
              <div className={Classes.contentImage}>
                {imagesSources && Boolean(imagesSources?.length) && (
                  <Image
                    src={imagesSources[0]}
                    srcSet={`${imagesSources[0]} 1x, ${imagesSources[1]} 2x`}
                    alt=""
                    loading="lazy"
                    fetchPriority="low"
                    decoding="async"
                    //@ts-expect-error -- DS issue
                    aspectRatio=""
                    fit
                  />
                )}
              </div>
            </div>
          </section>
          {isCarsSellPageEnabled && isMp && (
            <>
              <SellSteps />
              <SellValueChecker />
              <SellReasons />
              <SellTips />
              <section className="PageContent-card PageContent-offset">
                <SellFaqList />
              </section>
              <SellNudge />
            </>
          )}
          {isCarsSellPageEnabled && !location && (
            <section className="PageContent-card PageContent-offset">
              <Title4 as="h2" utilities={['marginBottomS']}>
                {t('cars.sell.locations.title')}
              </Title4>
              <ListBasic>
                {carsLocations.map(({ slug, name }) => (
                  <ListItemBasic key={slug}>
                    <Link href={`/cp/91/auto-verkopen/${slug}/`}>
                      {t('cars.sell.locations.label', { location: name })}
                    </Link>
                  </ListItemBasic>
                ))}
              </ListBasic>
            </section>
          )}
        </PageContent>
      </PageElement>
    </EnvironmentProvider>
  );
};

export default SellPageContent;
